/**
 * DICOM value constraints for schedule-item fields.
 *
 * WHY THIS EXISTS: every field here is emitted verbatim into a Modality Worklist C-FIND response
 * by the Gateway (MwlService.ToDicomDataset). fo-dicom validates on add, and the emission loop has
 * no per-item try/catch, so ONE non-conformant value throws mid-stream and tears down the whole
 * association. The modality gets no Success status and every item after the bad one is lost — and
 * because the connection drops, it presents as a network fault, so the site's IT or the modality
 * vendor gets blamed for what is a data problem.
 *
 * That is not hypothetical. Setting Neutered to "Altered" on a single appointment was reproduced
 * against a live Gateway and produced:
 *   "Connection closed unexpectedly: The peer closed the connection mid-transfer
 *    without releasing the association. [socket closed during phase=pdu]"
 *
 * The option values below are therefore the DICOM *defined terms*, not display text. Labels are
 * what the user reads; values are what goes on the wire.
 *
 * Lengths come from PS3.5 Table 6.2-1. They are wire-protocol constants, not product choices.
 */
/**
 * Patient Sex (0010,0040) and Patient Sex Neutered (0010,2203) used to live here as two flat
 * option lists: M / F / O, and the raw defined terms ALTERED / UNALTERED shown to the user as-is.
 *
 * They now live in `@/lib/patientSex`, because the vet-facing vocabulary is not a flat list. An
 * animal's sex is four clinical states plus "not recorded", the neuter labels depend on the chosen
 * sex (a female is spayed, a male is neutered), and "not recorded" has to stay expressible so the
 * form never asserts that an unrecorded patient is intact.
 *
 * The wire rule is unchanged and still absolute: the persisted values are the uppercase DICOM
 * defined terms, M / F / ALTERED / UNALTERED, or the field is absent. See PERSISTED_NEUTER_VALUES
 * in that module, and the reproduction in this file's header for what lowercase costs.
 */
/**
 * Requested Procedure Priority (0040,1003), VR SH.
 * PS3.4 C.4.11 enumerates STAT, HIGH, ROUTINE, MEDIUM, LOW. The form previously offered "ASAP",
 * which is not an enumerated value.
 */
export const PROCEDURE_PRIORITY_OPTIONS = [
    { value: 'STAT', labelKey: 'dicom.priority.stat' },
    { value: 'HIGH', labelKey: 'dicom.priority.high' },
    { value: 'ROUTINE', labelKey: 'dicom.priority.routine' },
    { value: 'MEDIUM', labelKey: 'dicom.priority.medium' },
    { value: 'LOW', labelKey: 'dicom.priority.low' },
];
